import { CartAddEvent, CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Cart drawer extras: upsell "Add" buttons and the shipping protection toggle.
 * Both post to the Ajax Cart API with the drawer's section id, then dispatch the
 * theme's cart events so cart-items-component morphs the drawer and the cart
 * icon updates its count.
 */

/**
 * @param {HTMLElement} el
 * @returns {string | undefined}
 */
function getSectionId(el) {
  const host = el.closest('cart-items-component');
  return host instanceof HTMLElement ? host.dataset.sectionId : undefined;
}

/**
 * @param {string | undefined} html
 * @returns {number}
 */
function readItemCount(html) {
  if (!html) return 0;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const text = doc.querySelector('[ref="cartItemCount"]')?.textContent;
  return text ? parseInt(text, 10) : 0;
}

/**
 * @param {string} url
 * @param {Record<string, unknown>} payload
 * @param {string | undefined} sectionId
 */
async function postCart(url, payload, sectionId) {
  const body = JSON.stringify(sectionId ? { ...payload, sections: sectionId } : payload);
  const response = await fetch(url, fetchConfig('json', { body }));
  const data = await response.json();
  if (!response.ok || data.status || data.errors) {
    throw new Error(data.description || data.message || 'Cart request failed');
  }
  return data;
}

/**
 * @param {HTMLElement} source
 * @param {string | undefined} sectionId
 * @param {any} data
 * @param {'add' | 'update'} kind
 */
function announce(source, sectionId, data, kind) {
  const html = sectionId ? data.sections?.[sectionId] : undefined;
  const detail = {
    itemCount: readItemCount(html),
    source: 'cart-drawer-extras',
    sections: data.sections,
  };
  const event = kind === 'add' ? new CartAddEvent({}, sectionId, detail) : new CartUpdateEvent({}, sectionId || '', detail);
  source.dispatchEvent(event);
}

class CartUpsell extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-upsell-variant]')) return;

    const card = select.closest('[data-upsell-card]');
    const option = select.selectedOptions[0];
    const priceEl = card?.querySelector('[data-upsell-price]');
    const button = card?.querySelector('[data-upsell-add]');
    if (!option || !(priceEl instanceof HTMLElement)) return;

    priceEl.replaceChildren();
    if (option.dataset.compare) {
      const s = document.createElement('s');
      s.className = 'cde-upsell__compare';
      s.textContent = option.dataset.compare;
      priceEl.append(s, ' ');
    }
    const span = document.createElement('span');
    span.textContent = option.dataset.price || '';
    priceEl.append(span);

    if (button instanceof HTMLElement) button.dataset.variantId = option.value;
  };

  /** @param {Event} event */
  #onClick = async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('[data-upsell-add]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const card = button.closest('[data-upsell-card]');
    const errorEl = card?.querySelector('[data-upsell-error]');
    const id = Number(button.dataset.variantId);
    if (!id) return;

    const label = button.textContent;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = this.dataset.addingLabel || label;
    if (errorEl instanceof HTMLElement) errorEl.hidden = true;

    const sectionId = getSectionId(this);
    try {
      const data = await postCart(Theme.routes.cart_add_url, { items: [{ id, quantity: 1 }] }, sectionId);
      announce(this, sectionId, data, 'add');
    } catch (error) {
      console.error(error);
      if (errorEl instanceof HTMLElement) {
        errorEl.textContent = this.dataset.errorLabel || String(error);
        errorEl.hidden = false;
      }
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.textContent = label;
    }
  };
}

const DECLINED_ATTRIBUTE = '_shipping_protection';

class CartProtection extends HTMLElement {
  static observedAttributes = ['data-protection-orphan', 'data-auto-add'];

  /** Stops retrying the automatic add after a failure on this page view. */
  static autoAddFailed = false;

  #busy = false;

  connectedCallback() {
    this.addEventListener('change', this.#onToggle);
    this.#sync();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onToggle);
  }

  attributeChangedCallback() {
    this.#sync();
  }

  #sync() {
    if (this.dataset.protectionOrphan === 'true') {
      this.#setProtection(false, { remember: false });
    } else if (this.dataset.autoAdd === 'true' && !CartProtection.autoAddFailed) {
      this.#setProtection(true, { remember: false, isAuto: true });
    }
  }

  /** @param {Event} event */
  #onToggle = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-protection-toggle]')) return;
    this.#setProtection(input.checked, { remember: true });
  };

  /**
   * Adds or removes shipping protection with a single cart/update request.
   * When the shopper switches it off, a cart attribute remembers the choice so it
   * is not added back automatically.
   * @param {boolean} on
   * @param {{ remember: boolean, isAuto?: boolean }} options
   */
  async #setProtection(on, { remember, isAuto = false }) {
    if (this.#busy) return;
    const variantId = this.dataset.variantId;
    if (!variantId) return;

    const input = this.querySelector('[data-protection-toggle]');
    const payload = { updates: { [variantId]: on ? 1 : 0 } };
    if (remember) payload.attributes = { [DECLINED_ATTRIBUTE]: on ? '' : 'declined' };

    this.#busy = true;
    if (input instanceof HTMLInputElement) input.disabled = true;
    const sectionId = getSectionId(this);
    try {
      const data = await postCart(Theme.routes.cart_update_url, payload, sectionId);
      announce(this, sectionId, data, 'update');
    } catch (error) {
      console.error(error);
      if (isAuto) CartProtection.autoAddFailed = true;
      if (input instanceof HTMLInputElement) input.checked = !on;
    } finally {
      this.#busy = false;
      if (input instanceof HTMLInputElement) input.disabled = false;
    }
  }
}

if (!customElements.get('cart-upsell')) customElements.define('cart-upsell', CartUpsell);
if (!customElements.get('cart-protection')) customElements.define('cart-protection', CartProtection);
